import { cx } from '@mage-ui/styled-system/css';
import {
  badge,
  icon,
  input,
  inputButton,
  inputDescription,
  inputError,
  inputFileGroup,
  inputFileInput,
  inputFilePlaceholder,
  inputFileSection,
  inputLabel,
  inputRoot,
  inputWrapper,
} from '@mage-ui/styled-system/recipes';
import { Badge, FileInput, Group } from '@mantine/core';
import { Icon } from '@/components/data-display';

export type InputFileProps = {
  classNames?: {
    root?: string;
    label?: string;
    description?: string;
    error?: string;
    wrapper?: string;
    input?: string;
    section?: string;
    placeholder?: string;
    clearButton?: string;
  };
  badgeClassName?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  multiple?: boolean;
  value?: File | File[] | null;
  required?: boolean;
  leftSection?: React.ReactNode;
  onChange?: (files: File | File[] | null) => void;
};

const ValueComponent = ({
  value,
  badgeClassName,
}: {
  value: File | File[] | null;
  badgeClassName?: string;
}) => {
  if (value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    return (
      <Group
        classNames={{
          root: cx('group', inputFileGroup()),
        }}
      >
        {value.map((file) => (
          <Badge
            classNames={{
              root: cx(badge({ status: 'neutral' }), badgeClassName),
            }}
            key={file.name}
          >
            {file.name}
          </Badge>
        ))}
      </Group>
    );
  }

  return (
    <Badge
      classNames={{ root: cx(badge({ status: 'neutral' }), badgeClassName) }}
    >
      {value.name}
    </Badge>
  );
};

export const InputFile = ({
  classNames,
  multiple,
  badgeClassName,
  ...props
}: InputFileProps) => {
  return (
    <FileInput
      classNames={{
        root: cx(classNames?.root ?? inputRoot(), 'group'),
        label: classNames?.label ?? inputLabel(),
        required: classNames?.label ?? inputLabel(),
        description: classNames?.description ?? inputDescription(),
        error: classNames?.error ?? inputError(),
        wrapper: classNames?.wrapper ?? inputWrapper(),
        input: classNames?.input ?? cx(input(), inputFileInput()),
        section: classNames?.section ?? inputFileSection(),
        placeholder: classNames?.placeholder ?? inputFilePlaceholder(),
      }}
      multiple={multiple}
      valueComponent={(props) => (
        <ValueComponent {...props} badgeClassName={badgeClassName ?? ''} />
      )}
      clearable
      clearButtonProps={{
        className:
          classNames?.clearButton ??
          inputButton({ variant: 'ghost', size: 's', color: 'danger' }),
        children: <Icon name='trash-01' classNames={icon({ size: 's' })} />,
      }}
      {...props}
    />
  );
};
